import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Calendar, X, Trash2 } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { EventoBottomSheet } from '@/modules/professional/components/EventoBottomSheet';
import { EventoQuickViewSheet } from '@/modules/professional/components/EventoQuickViewSheet';
import { PapeleraBottomSheet } from '@/modules/professional/components/PapeleraBottomSheet';
import { PushService } from '@/core/infrastructure/push/PushService';
import styles from './CalendarioSemanalScreen.module.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface EventoAgenda {
  id: string;
  inicio: string;
  fin: string;
  tipo: 'cita_digital' | 'cita_manual' | 'ocupado';
  titulo: string;
  subtitulo?: string;
  estado?: string;
  comprobante?: {
    comprobante_id: number;
    banco_emisor: string;
    referencia_6_digitos: string;
    telefono_emisor: string;
    monto_pagado: number;
    status_pago: string;
  } | null;
}

interface CitaDigitalRaw {
  cita_id: string;
  fecha_hora_inicio: string;
  fecha_hora_fin: string;
  estado: string;
  perfiles_usuario?: { nombres_completos: string; apellidos_completos: string }[] | null;
  cita_comprobantes_pago?: EventoAgenda['comprobante'][] | null;
}

interface PerfilUsuarioRaw {
  perfil_usuario_id: string;
  nombres_completos: string;
  apellidos_completos: string;
}

interface EventoManualRaw {
  evento_id: string;
  inicio: string;
  fin: string;
  tipo: 'cita_manual' | 'ocupado';
  nota?: string;
  paciente_uuid?: string;
  paciente_data?: { nombres_completos?: string } | null;
}

// ─── Utilidades de fecha ──────────────────────────────────────────────────────

const getWeekDates = (baseDate: Date): Date[] => {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const next = new Date(monday);
    next.setDate(monday.getDate() + i);
    return next;
  });
};

const formatHour = (timeStr: string): string => {
  const [h, m] = timeStr.split(':');
  const date = new Date();
  date.setHours(Number.parseInt(h), Number.parseInt(m));
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
};

// ─── Componentes Hijos ────────────────────────────────────────────────────────

const EventCard = ({ item, onUpdateStatus, onClick }: { item: EventoAgenda; onUpdateStatus?: (id: string, s: string) => void; onClick?: () => void }) => {
  const isDigital = item.tipo === 'cita_digital';
  const isManual = item.tipo === 'cita_manual';

  let eventColor = '#CBD5E1'; // default slate

  if (isDigital) {
    eventColor = '#3B82F6';
  } else if (isManual) {
    eventColor = '#10B981';
  }

  let statusBg = 'rgba(241, 245, 249, 0.8)';
  let statusBorder = 'rgba(226, 232, 240, 0.8)';
  let statusTextColor = '#475569';
  if (item.estado === 'pendiente_verificacion_pago') {
    statusBg = 'rgba(254, 243, 199, 0.8)';
    statusBorder = 'rgba(252, 211, 77, 0.8)';
    statusTextColor = '#D97706';
  } else if (item.estado === 'confirmada') {
    statusBg = 'rgba(209, 250, 229, 0.8)';
    statusBorder = 'rgba(110, 231, 183, 0.8)';
    statusTextColor = '#059669';
  } else if (item.estado === 'completada' || item.estado === 'atendida') {
    statusBg = 'rgba(219, 234, 254, 0.8)';
    statusBorder = 'rgba(147, 197, 253, 0.8)';
    statusTextColor = '#1D4ED8';
  } else if (item.estado === 'no_show_paciente') {
    statusBg = 'rgba(254, 226, 226, 0.8)';
    statusBorder = 'rgba(252, 165, 165, 0.8)';
    statusTextColor = '#DC2626';
  }

  const cardStyle: React.CSSProperties & { '--event-color'?: string } = {
    '--event-color': eventColor
  };

  return (
    <button 
      type="button"
      className={styles.eventCard} 
      onClick={onClick}
      disabled={!onClick}
      style={{ cursor: onClick ? 'pointer' : 'default', textAlign: 'left', width: '100%', background: 'none', border: 'none', padding: 0 }}
    >
      <div className={styles.timeColumn}>
        <span className={styles.timeText}>{formatHour(item.inicio)}</span>
        <span className={styles.timeSubtext}>{formatHour(item.fin)}</span>
      </div>
      <div className={styles.eventContent} style={cardStyle}>
        <div className={styles.eventHeader}>
          <span className={styles.eventTitle}>{item.titulo}</span>
        </div>
        {item.subtitulo ? <span className={styles.eventSub}>{item.subtitulo}</span> : null}
        {item.estado ? (
          <div className={styles.statusContainer}>
            <div className={styles.statusBadge} style={{ backgroundColor: statusBg, borderColor: statusBorder }}>
              <span className={styles.statusText} style={{ color: statusTextColor }}>
                {item.estado.replaceAll('_', ' ').toUpperCase()}
              </span>
            </div>
            
            <div className={styles.actionBtnGroup}>
              {item.estado === 'pendiente_verificacion_pago' && onUpdateStatus && isDigital && (
                <button type="button"
                  className={styles.btnActionVerificar}
                  onClick={(e) => { e.stopPropagation(); if (onUpdateStatus) onUpdateStatus(item.id, 'verificar'); }}
                >
                  Verificar Pago
                </button>
              )}

              {item.estado === 'confirmada' && onUpdateStatus && isDigital && (
                <>
                  <button type="button"
                    className={styles.btnActionAtendida}
                    onClick={(e) => { e.stopPropagation(); onUpdateStatus(item.id, 'completada'); }}
                  >
                    Atendida
                  </button>
                  <button type="button"
                    className={styles.btnActionAusente}
                    onClick={(e) => { e.stopPropagation(); onUpdateStatus(item.id, 'no_show_paciente'); }}
                  >
                    Ausente
                  </button>
                </>
              )}

              {(item.estado === 'completada' || item.estado === 'no_show_paciente') && onUpdateStatus && isDigital && (
                <button type="button"
                  className="mt-2 w-full bg-transparent hover:bg-slate-100 text-slate-500 hover:text-slate-800 border border-slate-200 font-bold py-2 px-3 rounded-lg text-[11px] transition-colors"
                  onClick={(e) => { e.stopPropagation(); onUpdateStatus(item.id, 'confirmada'); }}
                >
                  DESHACER ESTADO
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </button>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────

export function CalendarioSemanalScreen() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weekDates, setWeekDates] = useState<Date[]>([]);
  const [loading, setLoading] = useState(false);
  const [eventos, setEventos] = useState<EventoAgenda[]>([]);
  const [verificarCita, setVerificarCita] = useState<EventoAgenda | null>(null);
  const [notaRechazo, setNotaRechazo] = useState('');
  const [procesandoAccion, setProcesandoAccion] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [quickViewEvent, setQuickViewEvent] = useState<EventoAgenda | null>(null);
  const [papeleraVisible, setPapeleraVisible] = useState(false);
  const [limboTimer, setLimboTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [limboEvent, setLimboEvent] = useState<EventoAgenda | null>(null);

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const [toastMsg, setToastMsg] = useState<{title: string, type: 'error' | 'success' | 'undo', onUndo?: () => void} | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      nextWeek();
    } else if (isRightSwipe) {
      prevWeek();
    }
  };

  const showToast = (title: string, type: 'error' | 'success' | 'undo' = 'error', onUndo?: () => void) => {
    setToastMsg({ title, type, onUndo });
    if (type !== 'undo') {
      setTimeout(() => setToastMsg(null), 5000);
    }
  };

  const handleLimboUndo = () => {
    if (limboTimer) clearTimeout(limboTimer);
    if (limboEvent) {
      setEventos(prev => [...prev, limboEvent].sort((a, b) => a.inicio.localeCompare(b.inicio)));
    }
    setLimboEvent(null);
    setLimboTimer(null);
    setToastMsg(null);
  };

  const handleDeleteEvent = (eventId: string) => {
    const eventToDelete = eventos.find(e => e.id === eventId);
    if (!eventToDelete) return;

    // Interfaz Optimista: ocultar de inmediato
    setEventos(prev => prev.filter(e => e.id !== eventId));
    setQuickViewEvent(null);
    setLimboEvent(eventToDelete);
    showToast('Cita eliminada.', 'undo', handleLimboUndo);

    if (limboTimer) clearTimeout(limboTimer);
    const timer = setTimeout(async () => {
      setLimboEvent(null);
      setToastMsg(null);
      try {
        if (eventToDelete.tipo === 'cita_digital') {
          const { error } = await supabase
            .from('agendamiento_citas')
            .update({ estado: 'cancelada_paciente' })
            .eq('cita_id', eventId);
          if (error) throw error;
        } else {
          // Soft delete real modificando deleted_at en agenda_eventos
          const { error } = await supabase
            .from('agenda_eventos')
            .update({ deleted_at: new Date().toISOString() })
            .eq('evento_id', eventId);
          if (error) throw error;
        }
      } catch (e: unknown) {
        console.error(e);
        showToast('Error al eliminar de la base de datos.', 'error');
        fetchDataForDate(selectedDate);
      }
    }, 7000);
    setLimboTimer(timer);
  };

  useEffect(() => {
    // Al cargar la pantalla de calendario por primera vez, solicitamos permisos e indexamos push notifications.
    PushService.registerAndSubscribe().catch((err: unknown) => {
      console.warn('[CalendarioSemanalScreen] push registration fail:', err);
    });
  }, []);

  useEffect(() => {
    const dates = getWeekDates(selectedDate);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWeekDates(dates);
    fetchDataForDate(selectedDate);
  }, [selectedDate]);

  async function fetchDataForDate(date: Date) {
    setLoading(true);
    try {
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (!user || authErr) throw new Error('No user found');

      // Obtener el string de fecha local (YYYY-MM-DD) para los eventos manuales
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const localDateStr = `${year}-${month}-${day}`;

      // Calcular inicio y fin del día en hora local para consultar TIMESTAMPTZ en UTC
      const startOfDay = new Date(year, date.getMonth(), date.getDate(), 0, 0, 0);
      const endOfDay = new Date(year, date.getMonth(), date.getDate(), 23, 59, 59, 999);

      const { data: citasDigitales, error: errCitas } = await supabase
        .from('agendamiento_citas')
        .select(`
          cita_id, fecha_hora_inicio, fecha_hora_fin, estado,
          perfiles_usuario!agendamiento_citas_paciente_uuid_fkey(nombres_completos, apellidos_completos),
          cita_comprobantes_pago(comprobante_id, banco_emisor, referencia_6_digitos, telefono_emisor, monto_pagado, status_pago)
        `)
        .eq('profesional_uuid', user.id)
        .gte('fecha_hora_inicio', startOfDay.toISOString())
        .lte('fecha_hora_inicio', endOfDay.toISOString())
        .neq('estado', 'cancelada_paciente');

      const { data: eventosManuales, error: errEventos } = await supabase
        .from('agenda_eventos')
        .select(`
          evento_id, inicio, fin, tipo, nota, paciente_data, paciente_uuid
        `)
        .eq('profesional_uuid', user.id)
        .eq('fecha', localDateStr)
        .is('deleted_at', null);

      if (errCitas) console.error('Error citas:', errCitas);
      if (errEventos) console.error('Error eventos:', errEventos);

      // Fetch perfiles para las citas manuales que tengan un UUID de paciente asignado
      const pacienteUuids = eventosManuales?.map(e => e.paciente_uuid).filter(Boolean) || [];
      const perfilesMap: Record<string, PerfilUsuarioRaw> = {};
      if (pacienteUuids.length > 0) {
        const { data: perfiles } = await supabase
          .from('perfiles_usuario')
          .select('perfil_usuario_id, nombres_completos, apellidos_completos')
          .in('perfil_usuario_id', pacienteUuids);
        if (perfiles) {
          (perfiles as PerfilUsuarioRaw[]).forEach((p) => {
            perfilesMap[p.perfil_usuario_id] = p;
          });
        }
      }

      let merged: EventoAgenda[] = [];

      if (citasDigitales) {
        merged = [...merged, ...(citasDigitales as unknown as CitaDigitalRaw[]).map((c) => {
          const perfil = c.perfiles_usuario?.[0];
          return {
            id: c.cita_id,
            inicio: new Date(c.fecha_hora_inicio).toTimeString().split(' ')[0],
            fin: new Date(c.fecha_hora_fin).toTimeString().split(' ')[0],
            tipo: 'cita_digital' as const,
            titulo: perfil ? `${perfil.nombres_completos} ${perfil.apellidos_completos}` : 'Paciente Digital',
            subtitulo: 'Reserva Online',
            estado: c.estado,
            comprobante: c.cita_comprobantes_pago?.[0] ?? null
          };
        })];
      }

      if (eventosManuales) {
        merged = [...merged, ...(eventosManuales as EventoManualRaw[]).map((e) => {
          let pName = 'Bloqueo / Ocupado';
          if (e.tipo === 'cita_manual') {
            if (e.paciente_uuid && perfilesMap[e.paciente_uuid]) {
              const p = perfilesMap[e.paciente_uuid];
              pName = `${p.nombres_completos} ${p.apellidos_completos}`;
            } else if (e.paciente_data?.nombres_completos) {
              pName = `${e.paciente_data.nombres_completos}`;
            } else {
              pName = 'Agenda Presencial';
            }
          }
          return {
            id: e.evento_id,
            inicio: e.inicio,
            fin: e.fin,
            tipo: e.tipo,
            titulo: pName,
            subtitulo: e.tipo === 'cita_manual' ? '   Agenda Presencial' : (e.nota || 'Agenda bloqueada')
          };
        })];
      }

      // Ordenar por hora de inicio
      merged.sort((a, b) => a.inicio.localeCompare(b.inicio));
      setEventos(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async (decision: 'aprobado' | 'pago_parcial' | 'rechazado') => {
    if (!verificarCita) return;
    try {
      setProcesandoAccion(true);
      const { data, error } = await supabase.rpc('fn_aprobar_comprobante_pago', {
        p_cita_id: verificarCita.id,
        p_decision: decision,
        p_nota: notaRechazo || null
      });
      if (error) throw error;
      if (data?.success === false) {
        throw new Error(data.message || 'Error al procesar el pago.');
      }
      setVerificarCita(null);
      setNotaRechazo('');
      fetchDataForDate(selectedDate);
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Error al procesar el pago.';
      showToast(msg);
    } finally {
      setProcesandoAccion(false);
    }
  };

  const handleUpdateStatus = async (citaId: string, newStatus: string) => {
    if (newStatus === 'verificar') {
      const cita = eventos.find(e => e.id === citaId);
      if (cita) {
        setVerificarCita(cita);
      }
      return;
    }
    
    try {
      setLoading(true);
      const { error } = await supabase
        .from('agendamiento_citas')
        .update({ estado: newStatus })
        .eq('cita_id', citaId);
        
      if (error) throw error;
      fetchDataForDate(selectedDate);
    } catch (err) {
      console.error(err);
      showToast('Error al actualizar el estado de la cita. Es posible que haya un conflicto con otra actualización.');
      setLoading(false);
    }
  };

  const nextWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 7);
    setSelectedDate(d);
  };

  const prevWeek = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 7);
    setSelectedDate(d);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[250px] text-slate-500">
          <div className={styles.spinner} />
        </div>
      );
    }
    if (eventos.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Calendar size={48} color="#CBD5E1" />
          <p className={styles.emptyText}>No hay eventos registrados para este día.</p>
        </div>
      );
    }
    return (
      <div className={styles.listContent}>
        {eventos.map((item) => (
          <EventCard 
            key={item.id} 
            item={item} 
            onUpdateStatus={handleUpdateStatus} 
            onClick={() => setQuickViewEvent(item)}
          />
        ))}
      </div>
    );
  };

  let toastColor = 'bg-emerald-500';
  if (toastMsg?.type === 'error') toastColor = 'bg-rose-500';
  else if (toastMsg?.type === 'undo') toastColor = 'bg-amber-400';

  return (
    <div 
      className={`${styles.container} min-h-screen relative`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEndHandler}
    >
      {toastMsg && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-5 py-3 rounded-2xl flex items-center gap-3 shadow-xl z-50 animate-in fade-in slide-in-from-bottom-5">
          <div className={`w-2 h-2 rounded-full ${toastColor}`} />
          <span className="text-[13px] font-medium flex-1 tracking-wide">{toastMsg.title}</span>
          
          {toastMsg.type === 'undo' && toastMsg.onUndo ? (
            <button type="button" 
              onClick={toastMsg.onUndo} 
              className="ml-3 bg-slate-700 hover:bg-slate-600 text-amber-400 font-bold px-3 py-1.5 rounded-lg text-xs transition-colors border-none cursor-pointer"
            >
              DESHACER
            </button>
          ) : null}

          <button type="button" onClick={() => {
            setToastMsg(null);
            if (toastMsg.type === 'undo' && limboTimer) {
              clearTimeout(limboTimer);
              handleDeleteEvent(limboEvent!.id);
            }
          }} className="text-slate-400 hover:text-white p-1 rounded-md transition-colors cursor-pointer border-none bg-transparent">
            <X size={16} />
          </button>
        </div>
      )}
      {/* HEADER */}
      <div className="flex items-center justify-between px-6 py-5 sticky top-0 z-40 bg-slate-50/60 backdrop-blur-md border-b border-slate-200/50">
        <button type="button"
          className={styles.backBtn}
          onClick={() => { if (globalThis.history.length > 2) navigate(-1); else navigate('/hub'); }}
          aria-label="Volver"
          style={{ marginLeft: '8px' }}
        >
          <ArrowLeft size={22} color="#0F172A" />
        </button>
        <h1 className="text-xl font-extrabold text-slate-800 tracking-tight m-0">Mi Calendario</h1>
        <button type="button"
          onClick={() => setPapeleraVisible(true)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          aria-label="Ver Papelera"
        >
          <Trash2 size={22} color="#0F172A" />
        </button>
      </div>

      {/* WEEK NAVIGATOR */}
      <div className="flex items-center justify-between px-6 py-4">
        <button type="button" onClick={prevWeek} className="p-2 rounded-full hover:bg-slate-200/50 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
          <ChevronLeft size={22} color="#475569" />
        </button>
        <div className="relative flex items-center">
          <input 
            type="date" 
            value={selectedDate.toISOString().split('T')[0]} 
            onChange={(e) => {
              if (e.target.value) {
                const parts = e.target.value.split('-');
                const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                setSelectedDate(d);
              }
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            style={{ zIndex: 10 }}
          />
          <span className="text-lg font-bold text-slate-700 capitalize tracking-tight px-4 py-1 rounded-lg hover:bg-slate-200/50 transition-colors flex items-center gap-2 cursor-pointer">
            {selectedDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
            <Calendar size={18} className="text-slate-500" />
          </span>
        </div>
        <button type="button" onClick={nextWeek} className="p-2 rounded-full hover:bg-slate-200/50 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
          <ChevronRight size={22} color="#475569" />
        </button>
      </div>

      {/* DAYS SELECTOR */}
      <div className="flex justify-around px-4 pb-6">
        {weekDates.map((date) => {
          const isSelected = date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth();
          const dayName = date.toLocaleDateString('es-ES', { weekday: 'short' }).charAt(0).toUpperCase();
          return (
            <button type="button"
              key={date.getTime()}
              className={`${styles.dayChip} ${isSelected ? styles.dayChipSelected : ''}`}
              onClick={() => setSelectedDate(new Date(date))}
            >
              <span className={`${styles.dayName} ${isSelected ? styles.dayNameSelected : ''}`}>{dayName}</span>
              <span className={`${styles.dayNumber} ${isSelected ? styles.dayNumberSelected : ''}`}>{date.getDate()}</span>
            </button>
          );
        })}
      </div>

      {/* TIMELINE */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>

      {/* FAB - ADD EVENT */}
      <button type="button" className={styles.fab} onClick={() => setSheetVisible(true)}>
        <Plus size={28} color="#FFFFFF" />
      </button>

      {/* BOTTOM SHEET */}
      <EventoBottomSheet
        visible={sheetVisible}
        onClose={() => setSheetVisible(false)}
        selectedDate={selectedDate}
        onEventCreated={() => fetchDataForDate(selectedDate)}
      />

      <EventoQuickViewSheet
        evento={quickViewEvent}
        onClose={() => setQuickViewEvent(null)}
        onDelete={(id) => handleDeleteEvent(id)}
      />

      <PapeleraBottomSheet
        visible={papeleraVisible}
        onClose={() => setPapeleraVisible(false)}
        onRestore={() => {
          fetchDataForDate(selectedDate);
        }}
      />

      {/* MODAL VERIFICACIÓN COMPROBANTE PAGO */}
      {verificarCita && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Verificar Pago Móvil</h3>
            <p className={styles.modalSubtitle}>Valida los datos ingresados por el paciente antes de confirmar la cita.</p>
            
            {verificarCita.comprobante ? (
              <div className={styles.paymentInfoBox}>
                <div className={styles.paymentInfoRow}>
                  <span className={styles.infoLabel}>Banco Emisor:</span>
                  <span className={styles.infoValue}>{verificarCita.comprobante.banco_emisor}</span>
                </div>
                <div className={styles.paymentInfoRow}>
                  <span className={styles.infoLabel}>Referencia:</span>
                  <span className={styles.infoValue}>{verificarCita.comprobante.referencia_6_digitos}</span>
                </div>
                <div className={styles.paymentInfoRow}>
                  <span className={styles.infoLabel}>Teléfono:</span>
                  <span className={styles.infoValue}>{verificarCita.comprobante.telefono_emisor}</span>
                </div>
                <div className={styles.paymentInfoRow}>
                  <span className={styles.infoLabel}>Monto Pagado:</span>
                  <span className={styles.infoValue} style={{ color: '#10B981', fontWeight: 700 }}>
                    {Number(verificarCita.comprobante.monto_pagado).toLocaleString('es-VE', { style: 'currency', currency: 'VES' })}
                  </span>
                </div>
              </div>
            ) : (
              <div className={styles.noPaymentBox}>
                <span>No hay información de comprobante disponible para esta cita.</span>
              </div>
            )}

            <div style={{ marginTop: '24px' }}>
              <label htmlFor="nota_profesional" style={{ fontSize: '14px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>Nota del Profesional (Opcional)</label>
              <textarea
                id="nota_profesional"
                placeholder="Ej. Pago incompleto, faltan $5"
                value={notaRechazo}
                onChange={(e) => setNotaRechazo(e.target.value)}
                className={styles.modalTextArea}
              />
            </div>

            <div className={styles.modalActions}>
              <button type="button" 
                onClick={() => { setVerificarCita(null); setNotaRechazo(''); }}
                className={`${styles.modalBtn} ${styles.modalBtnCerrar}`}
                disabled={procesandoAccion}
              >
                Cerrar
              </button>
              
              <button type="button" 
                onClick={() => handleProcessPayment('rechazado')}
                className={`${styles.modalBtn} ${styles.modalBtnRechazar}`}
                disabled={procesandoAccion}
              >
                Rechazar
              </button>

              <button type="button" 
                onClick={() => handleProcessPayment('pago_parcial')}
                className={`${styles.modalBtn} ${styles.modalBtnPagoParcial}`}
                disabled={procesandoAccion}
              >
                Pago Parcial
              </button>

              <button type="button" 
                onClick={() => handleProcessPayment('aprobado')}
                className={`${styles.modalBtn} ${styles.modalBtnAprobar}`}
                disabled={procesandoAccion}
              >
                {procesandoAccion ? 'Aprobando...' : 'Aprobar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
